import { createApiClient } from '@betstarters/api'
import { getAccessToken, setTokens, clearTokens } from '@betstarters/api'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { demoResolve } from '../data/demo-dataset'

// VITE_DEMO=1 turns the whole app into a self-contained, backend-free demo: the
// axios adapter below answers every request from src/data/demo-dataset.ts, so a
// laptop with `npm run dev` is enough for a client presentation — no ClickHouse,
// Redpanda or bs-iam needed.
export const DEMO_MODE = import.meta.env.VITE_DEMO === '1'

// VITE_API_URL is the API root (e.g. https://bs-staging-reporting-api.*.azurecontainerapps.io).
// All reporting endpoints live under /api/v1, so we append it here — keeps the env var
// identical to the one the ingestor uses and avoids per-env path juggling.
const apiRoot = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const api = createApiClient({
  baseURL: `${apiRoot}/api/v1`,
  loginPath: '/login',
})

if (DEMO_MODE) {
  // Pretend we are already authenticated so the AuthProvider hydrates the demo
  // user from /auth/me (resolved below) and skips the login screen.
  setTokens('demo.access.token', 'demo.refresh.token')

  // Replace the HTTP adapter entirely: no request ever leaves the browser.
  api.defaults.adapter = async (config: InternalAxiosRequestConfig): Promise<AxiosResponse> => {
    const url = config.url ?? ''
    const params = (config.params ?? {}) as Record<string, unknown>
    const body = demoResolve(url, params)
    // Small delay so loading states are visible during a live demo.
    await new Promise((r) => setTimeout(r, 120))
    return {
      data: body ?? { data: [] },
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
    } as AxiosResponse
  }
}

// Timezone header injection
let timezone = localStorage.getItem('reporting_timezone') || 'UTC'

api.interceptors.request.use((config) => {
  config.headers['X-Timezone'] = timezone
  return config
})

export function setTimezone(tz: string) {
  timezone = tz
  localStorage.setItem('reporting_timezone', tz)
}

export function getTimezone(): string {
  return timezone
}

// Re-export token functions for backward compatibility
export { getAccessToken, setTokens, clearTokens }

// --- Report endpoints (keep existing API surface) ---

export async function login(email: string, password: string) {
  const response = await api.post('/auth/login', { email, password })
  return response.data
}

export async function getProfile() {
  const response = await api.get('/auth/me')
  return response.data
}

export async function getTenants() {
  const response = await api.get('/me/tenants')
  return response.data
}

export async function changePassword(currentPassword: string, newPassword: string) {
  const response = await api.post('/auth/me/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
  return response.data
}

export async function getGGRReport(params: { date_from: string; date_to: string; group_by?: string; site_ids?: string; display_currency?: string }) {
  const response = await api.get('/reports/ggr', { params })
  return response.data
}

export async function getDepositReport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; payment_method_type?: string; granularity?: string }) {
  const response = await api.get('/reports/cashier/deposits', { params })
  return response.data
}

export async function getWithdrawalReport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; payment_method_type?: string; granularity?: string }) {
  const response = await api.get('/reports/cashier/withdrawals', { params })
  return response.data
}

export async function getCashierOverview(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; payment_method_type?: string; granularity?: string }) {
  const response = await api.get('/reports/cashier/overview', { params })
  return response.data
}

export async function getCashierMix(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; payment_method_type?: string; dimensions?: string }) {
  const response = await api.get('/reports/cashier/mix', { params })
  return response.data
}

export async function getFTDReport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; payment_method_type?: string; granularity?: string }) {
  const response = await api.get('/reports/cashier/ftd', { params })
  return response.data
}

export async function getPlayerCashier(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; player_search?: string; ftd_only?: boolean; limit?: number; offset?: number }) {
  const response = await api.get('/reports/cashier/players', { params })
  return response.data
}

export async function getPlayerLeaderboard(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string; limit?: number }) {
  const response = await api.get('/reports/players/leaderboard', { params })
  return response.data
}

export async function getCashierTransactions(params: { date_from: string; date_to: string; site_ids?: string; payment_method_type?: string; player_id?: string; event_types?: string; limit?: number; offset?: number }) {
  const response = await api.get('/reports/cashier/transactions', { params })
  return response.data
}

export async function getActivePlayers(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string }) {
  const response = await api.get('/reports/players/active', { params })
  return response.data
}

export async function getBonusReport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string }) {
  const response = await api.get('/reports/bonus', { params })
  return response.data
}

export async function getSportsbookReport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string }) {
  const response = await api.get('/reports/sportsbook/overview', { params })
  return response.data
}

export async function getSportsbookBySport(params: { date_from: string; date_to: string; site_ids?: string; display_currency?: string }) {
  const response = await api.get('/reports/sportsbook/by-sport', { params })
  return response.data
}

export async function getCohortRetention(params: { cohort_month: string; site_ids?: string }) {
  const response = await api.get('/reports/cohorts/retention', { params })
  return response.data
}

export interface GameReportParams {
  date_from: string;
  date_to: string;
  site_ids?: string;
  display_currency?: string;
  period?: string;
  game_type?: string;
  brand_id?: string;
  limit?: number;
  offset?: number;
  top_n?: number;
  sort_by?: string;
  sort_dir?: string;
  game_id?: number;
  dimensions?: string;
}

export async function getGamePerformance(params: GameReportParams) {
  const response = await api.get('/reports/games/performance', { params })
  return response.data
}

export async function getBrandMargin(params: GameReportParams) {
  const response = await api.get('/reports/brands/margin', { params })
  return response.data
}

export async function getGameOverview(params: GameReportParams) {
  const response = await api.get('/reports/games/overview', { params })
  return response.data
}

export async function getGameTypeMix(params: GameReportParams) {
  const response = await api.get('/reports/games/mix', { params })
  return response.data
}

export async function getTopGames(params: GameReportParams) {
  const response = await api.get('/reports/games/top', { params })
  return response.data
}

export async function getGamePlayers(params: GameReportParams) {
  const response = await api.get('/reports/games/players', { params })
  return response.data
}

// --- Player Detail endpoints ---

export interface PlayerDetailParams {
  player_id: string;
  site_id: number;
  date_from?: string;
  date_to?: string;
  display_currency?: string;
  limit?: number;
  offset?: number;
}

export async function getPlayerProfile(params: PlayerDetailParams) {
  const response = await api.get('/reports/players/detail/profile', { params })
  return response.data
}

export async function getPlayerSummary(params: PlayerDetailParams) {
  const response = await api.get('/reports/players/detail/summary', { params })
  return response.data
}

export async function getPlayerCasinoGames(params: PlayerDetailParams) {
  const response = await api.get('/reports/players/detail/casino', { params })
  return response.data
}

export async function getPlayerSportBreakdown(params: PlayerDetailParams) {
  const response = await api.get('/reports/players/detail/sport', { params })
  return response.data
}

export async function getPlayerDailyActivity(params: PlayerDetailParams) {
  const response = await api.get('/reports/players/detail/activity', { params })
  return response.data
}

export async function exportCSV(params: { report_type: string; date_from: string; date_to: string; site_ids?: string }) {
  const response = await api.get('/reports/export/csv', { params, responseType: 'blob' })
  return response.data
}

export async function exportXLSX(params: { report_type: string; date_from: string; date_to: string; site_ids?: string }) {
  const response = await api.get('/reports/export/xlsx', { params, responseType: 'blob' })
  return response.data
}

export async function executeAdHocQuery(query: Record<string, unknown>) {
  const response = await api.post('/query', query)
  return response.data
}

export async function getAlerts() {
  const response = await api.get('/alerts')
  return response.data
}

export async function createAlert(alert: unknown) {
  const response = await api.post('/alerts', alert)
  return response.data
}

export async function updateAlert(id: string | number, alert: unknown) {
  const response = await api.put('/alerts/rule', { ...alert as object, id: Number(id) })
  return response.data
}

export async function deleteAlert(id: string | number) {
  await api.delete(`/alerts/rule?id=${id}`)
}

export async function getAlertHistory(params: { site_id: number; from: string; to: string }) {
  const response = await api.get('/alerts/history', { params })
  return response.data
}

export default api
